import isEmpty from 'lodash/isEmpty'
import { CartItems, MappedPageName, MonetateKeys, PageName } from '@/types/monetate'
import { ORDER_ID } from './../constants/parameters'
import wcs from '@/scripts/libs/wcs'
import { Product } from '@/types/pdp'
import { PlpProduct } from '@/types/plp'
import { TRACKING_CART } from '@/scripts/constants/cookies'

const { currency, viewTaskName } = wcs.getStoreConfig()
export class MonetateHelper {
  private vuexStore: any
  private parsedCartItemsInStorage: CartItems | null = null

  private readonly mappedPageName: MappedPageName | ''
  private readonly pageType: string

  constructor() {
    window.monetateQ = window.monetateQ || []
    this.mappedPageName = this.getMappedPageName()
    this.pageType =
      (document.getElementById('pageType') as HTMLInputElement | undefined)?.value ?? ''
  }

  initMonetate = ({ vuexStore }: { vuexStore: any }) => {
    this.vuexStore = vuexStore
    this.setParsedCartItemsInStorage()
    this.removeTrackingCartInThankYouPage()
    if (this.isInCheckoutPage() || wcs.isInFluidPcp()) {
      this.reTrack()
    }
  }

  private setParsedCartItemsInStorage() {
    const cartItemsInStorage = localStorage.getItem(TRACKING_CART)
    this.parsedCartItemsInStorage = cartItemsInStorage ? JSON.parse(cartItemsInStorage) : null
  }

  private removeTrackingCartInThankYouPage() {
    if (wcs.isInTYP()) {
      localStorage.removeItem(TRACKING_CART)
    }
  }

  private isInCheckoutPage() {
    return wcs.isInDelivery() || wcs.isInPaymentPage() || wcs.isInTYP()
  }

  private pushTrackData = () => {
    window.monetateQ.push([MonetateKeys.TRACK_DATA])
  }

  private addProducts = () => {
    const products: PlpProduct[] = this.vuexStore.getters['plp/products']
    if (isEmpty(products)) {
      return undefined
    }

    const preperedForMonetate = products.map((product) => {
      return {
        productId: product.colors[0].catEntryId,
        sku: product.colors[0].partNumber,
      }
    })
    window.monetateQ.push([MonetateKeys.ADD_PRODUCTS, preperedForMonetate])
  }

  private addProductDetails = () => {
    const selectedProduct: Product = this.vuexStore.getters['pdp/selectedProduct']

    if (isEmpty(selectedProduct)) {
      return undefined
    }

    const productDetails = {
      productId: selectedProduct.catEntryId,
      sku: selectedProduct.partNumber,
    }
    window.monetateQ.push([MonetateKeys.ADD_PRODUCT_DETAILS, [productDetails]])
  }

  private addCategories = (category: string) => {
    window.monetateQ.push([MonetateKeys.ADD_CATEGORIES, [category]])
  }

  private addBreadcrumbs = (category: string) => {
    const breadcrumbsLabels = this.getBreadcrumbs(category)
    window.monetateQ.push([MonetateKeys.ADD_BREADCRUMBS, [...breadcrumbsLabels]])
  }

  private getBreadcrumbs = (category: string) => {
    if (wcs.isInPdp()) {
      return this.getBreadcrumbsInPDP()
    }

    if (wcs.isInPlp()) {
      const splitPageType = this.pageType?.split('_')
      return ['Home', ...splitPageType]
    }

    return [category]
  }

  private getBreadcrumbsInPDP = () => {
    const breadcrumbs: Record<string, string>[] = this.vuexStore.getters['pdp/breadcrumbs']
    const labels = breadcrumbs.map((breadcrumb) => breadcrumb?.label)
    return labels
  }

  private addCartRows = () => {
    if (wcs.isInTYP()) {
      return undefined
    }

    const productsInCart: CartItems = this.isInCheckoutPage()
      ? this.parsedCartItemsInStorage
      : this.vuexStore.getters.cartItems
    const mappedProducts = productsInCart?.map((item: any) => {
      return {
        productId: item?.catentryId,
        quantity: String(parseInt(item?.orderItemQuantity)),
        unitPrice: String(item?.price),
        currency,
      }
    })
    window.monetateQ.push([MonetateKeys.ADD_CART_ROWS, mappedProducts])
  }

  private addPurchaseRows = () => {
    if (!wcs.isInTYP()) {
      return undefined
    }

    const purchaseId = this.getOrderId()
    const purchaseRows = this.parsedCartItemsInStorage?.map((item: any) => {
      return {
        purchaseId,
        productId: item?.catentryId,
        quantity: String(parseInt(item?.orderItemQuantity)),
        unitPrice: String(item?.price),
        currency,
      }
    })
    window.monetateQ.push([MonetateKeys.ADD_PURCHASE_ROWS, purchaseRows])
  }

  private getOrderId() {
    const orderIdByName = (document.getElementsByName(ORDER_ID)[0] as HTMLInputElement | undefined)
      ?.value
    const orderIdByValue = (document.getElementById(ORDER_ID) as HTMLInputElement | undefined)
      ?.value

    return orderIdByName || orderIdByValue || ''
  }

  private getMappedPageName() {
    const { pageName } = wcs.getStoreConfig()
    return pageName ? this.mapPageName(pageName) : ''
  }

  private mapPageName(pageName: string) {
    switch (pageName) {
      case PageName.HOME_PAGE:
        return MappedPageName.MAIN
      case PageName.PDP:
      case PageName.PRODUCT_PAGE:
        return MappedPageName.PRODUCT
      case PageName.PLP:
      case PageName.PRODUCT_LISTING:
        return MappedPageName.CATEGORY
      case PageName.SHOP_CART:
      case PageName.SHOPPING_CART:
        return MappedPageName.CART
      case PageName.CORE_MEDIA:
        return MappedPageName.LANDING
      case PageName.CHECKOUT_LOGON:
      case PageName.DELIVERY:
      case PageName.DELIVERY_PAGE:
        return MappedPageName.SHIPPING
      case PageName.PAYMENT:
      case PageName.PAYMENT_REDESIGN:
      case PageName.PAYMENT_PAGE:
        return MappedPageName.BILLING
      case PageName.CONFIRMATION:
      case PageName.THANK_YOU_PAGE:
        return MappedPageName.PURCHASE
      case PageName.REMIX_LANDING:
        return MappedPageName.CUSTOM
      case PageName.REMIX_PRODUCT_DISPLAY:
        return MappedPageName.CUSTOM_PRODUCT
      case PageName.SEARCH_DISPLAY:
      case PageName.SEARCH_RESULTS:
        return MappedPageName.SEARCH
      case PageName.SORRY_PAGE:
        return MappedPageName.SORRY_PAGE
      default:
        return MappedPageName.OTHER_PAGE
    }
  }

  private setPage = () => {
    const pageName = this.getPageName()
    window.monetateQ.push([MonetateKeys.SET_PAGE_TYPE, pageName])
  }

  private getPageName = () => {
    const pageName = this.mappedPageName || this.pageType || viewTaskName
    return pageName
  }

  private setTrackingCartForCheckoutPages() {
    if (wcs.isInCart()) {
      localStorage.setItem(TRACKING_CART, JSON.stringify(this.vuexStore.getters.cartItems))
    }
  }

  private getCategory() {
    const category = this.pageType || this.mappedPageName || viewTaskName

    if (wcs.isInPdp()) {
      const breadcrumbsInPDP = this.getBreadcrumbsInPDP()
      return breadcrumbsInPDP.length > 1 ? breadcrumbsInPDP[2] : category
    }

    return category
  }

  reTrack = () => {
    const category = this.getCategory()
    this.setTrackingCartForCheckoutPages()

    this.setPage()
    this.addProducts()
    this.addProductDetails()
    this.addCategories(category)
    this.addBreadcrumbs(category)
    this.addCartRows()
    this.addPurchaseRows()
    this.pushTrackData()
  }

  public static generateModule() {
    if (window.MonetateHelper) {
      return window.MonetateHelper
    }

    return new MonetateHelper()
  }
}

window.MonetateHelper = MonetateHelper.generateModule()

export default window.MonetateHelper
